/* eslint-disable max-len */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';

export default class ComparisonNode extends BaseNode {
    operator: string;

    constructor() {
        super();
        this.linkedFeature = 'general';
        this.name = 'IF Condition';
        this.icon = 'TbArrowsSplit2';
        this.id = 'node_comparison';

        this.inputs = [
            {
                name: 'Compare',
                portId: 'comp',
                params: [
                    {
                        name: 'operand1',
                        dataType: TibboDataType.CUSTOM,
                        default: '',
                    },
                    {
                        name: 'operator',
                        dataType: TibboDataType.CUSTOM,
                        default: '=',
                    },
                    {
                        name: 'operand2',
                        dataType: TibboDataType.CUSTOM,
                        default: '',
                    },
                ],
            },
        ];

        this.outputs = [
            {
                name: 'True',
                portId: 'true_output',
                params: [],
            },
            {
                name: 'False',
                portId: 'false_output',
                params: [],
            },
        ];
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        switch (settingId) {
            case 'operator':
                return [
                    {
                        name: 'Equal To',
                        value: '=',
                    },
                    {
                        name: 'Not Equal To',
                        value: '<>',
                    },
                    {
                        name: 'Greater Than',
                        value: '>',
                    },
                    {
                        name: 'Greater Than or Equal to',
                        value: '>=',
                    },
                    {
                        name: 'Less Than',
                        value: '<',
                    },
                    {
                        name: 'Less Than or Equal To',
                        value: '<=',
                    },
                ];
            default:
                return super.getSettingOptions(settingId);
        }
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const links = project.getNodeLinks(this, 'comp');
            if (links.length > 0) {
                const link = links[0];
                const op1 = project.getNodeParameterValue(this, 'operand1');
                const op2 = project.getNodeParameterValue(this, 'operand2');
                const OPERATOR = project.getNodeParameter(link, 'operator');
                return `IF ${op1} ${OPERATOR} ${op2}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const links = project.getNodeLinks(this, 'comp');
        if (links.length > 0) {
            const link = links[0];
            const op1 = project.getNodeParameterValue(this, 'operand1');
            const op2 = project.getNodeParameterValue(this, 'operand2');
            const OPERATOR = project.getNodeParameter(link, 'operator');
            return `IF ${op1} ${OPERATOR} ${op2}`;
        }
        return super.getName();
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return `#### Condition
Performs an equality/inequality check between two values.
`;
    }
}
